// src/components/SurveyForm/Questions/LikertFaceQuestion/LikertFaceQuestion.jsx

import React, { Fragment } from 'react';
import {
  Box,
  Divider,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import DOMPurify from 'dompurify';
import PropTypes from 'prop-types';

/**
 * Componente para preguntas de escala Likert con iconos/caras
 * Muestra opciones con imágenes representativas (emojis, caras, etc.)
 */
const LikertFaceQuestion = ({
  questionName,
  description,
  options = [],
  currentValue = '',
  questionId,
  typeQuestion,
  index,
  hasError = false,
  primaryColor = '#1976d2',
  questionRef,
  getLikertIcon,
  onValueChange,
  hasOwnBold,
}) => {
  /**
   * Maneja el cambio de selección
   * @param {Event} event - Evento del radio button
   */
  const handleChange = (event) => {
    const selectedValue = event.target.value;
    onValueChange(selectedValue);
  };

  /**
   * Renderiza una opción individual con su icono
   */
  const renderOption = (option) => {
    const { numberOption, optionName } = option;
    const iconSrc = getLikertIcon(optionName);

    return (
      <FormControlLabel
        key={numberOption}
        value={optionName}
        control={
          <Radio
            sx={{
              color: hasError ? 'red' : 'rgba(0, 0, 0, 0.6)',
              '&.Mui-checked': {
                color: primaryColor,
              },
              // Ocultar el radio button visual, solo mostrar el icono
              '& .MuiSvgIcon-root': {
                fontSize: 0, // Hace invisible el círculo del radio
              },
            }}
          />
        }
        labelPlacement="bottom"
        label={
          <Box
            sx={{
              textAlign: 'center',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '0.5em',
            }}
          >
            {iconSrc ? (
              <Tooltip title={optionName} arrow>
                <img
                  src={iconSrc}
                  alt={optionName}
                  style={{
                    width: '3em',
                    height: '3em',
                    verticalAlign: 'middle',
                    cursor: 'pointer',
                    transition: 'transform 0.2s ease',
                    transform:
                      currentValue === optionName ? 'scale(1.2)' : 'scale(1)',
                    filter:
                      currentValue === optionName
                        ? 'none'
                        : 'grayscale(30%) opacity(0.8)',
                  }}
                  onError={(e) => {
                    e.target.src = '/placeholder.svg';
                  }}
                />
              </Tooltip>
            ) : (
              <Typography
                variant="caption"
                sx={{
                  fontSize: '0.75em',
                  textAlign: 'center',
                  maxWidth: '5em',
                  wordWrap: 'break-word',
                }}
              >
                {optionName}
              </Typography>
            )}
          </Box>
        }
        sx={{
          margin: '0 0.5em',
          '& .MuiFormControlLabel-label': {
            fontSize: '0.85em',
          },
        }}
      />
    );
  };

  return (
    <Fragment>
      {/* Título de la pregunta */}
      <FormLabel
        id={`${questionId}-${typeQuestion}`}
        style={{
          fontSize: '1.1em',
          fontWeight: hasOwnBold(questionName) ? 'normal' : 'bold',
          marginBottom: '0.8em',
          color: hasError ? 'red' : 'rgba(0, 0, 0, 0.6)',
        }}
        ref={questionRef}
      >
          <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(questionName) }} />
      </FormLabel>

      {/* Descripción opcional */}
      {description && (
        <Typography
          variant="caption"
          style={{ display: 'block', fontStyle: 'italic' }}
        >
          <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(description) }} />
        </Typography>
      )}

      {/* Grupo de opciones */}
      <RadioGroup
        name={`${questionId}-${typeQuestion}`}
        value={currentValue}
        onChange={handleChange}
        row
        style={{
          justifyContent: 'center',
          margin: '1.2em 0',
          flexWrap: 'wrap',
        }}
      >
        {options.map((option) => renderOption(option))}
      </RadioGroup>

      {/* Mensaje de error */}
      {hasError && (
        <Typography
          variant="caption"
          style={{
            display: 'block',
            textAlign: 'center',
            color: 'red',
            marginTop: '-0.5em',
            marginBottom: '0.5em',
            fontWeight: 'bold',
          }}
        >
          Por favor, selecciona una opción
        </Typography>
      )}

      <Divider variant="middle" />
    </Fragment>
  );
};

LikertFaceQuestion.propTypes = {
  questionName: PropTypes.string.isRequired,
  description: PropTypes.string,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      numberOption: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        .isRequired,
      optionName: PropTypes.string.isRequired,
    })
  ).isRequired,
  currentValue: PropTypes.string,
  questionId: PropTypes.number.isRequired,
  typeQuestion: PropTypes.string.isRequired,
  index: PropTypes.number.isRequired,
  hasError: PropTypes.bool,
  primaryColor: PropTypes.string,
  questionRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.any }),
  ]),
  getLikertIcon: PropTypes.func.isRequired,
  onValueChange: PropTypes.func.isRequired,
};

export default LikertFaceQuestion;